import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { FieldEngineerSummary } from '@/store/parentTicket'
import OverviewSectionCard from './OverviewSectionCard'

function EngineerRow({
  label,
  engineer,
}: {
  label: string
  engineer: FieldEngineerSummary
}) {
  const colors = useThemeColors()
  const name = engineer.full_name ?? '—'
  const initial = (name.trim()[0] ?? '?').toUpperCase()

  return (
    <HStack align="flex-start" spacing={3}>
      <Box
        w="36px"
        h="36px"
        borderRadius="full"
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        display="grid"
        placeItems="center"
        flexShrink={0}
      >
        <Text fontWeight="800" color="blue.600" fontSize="sm">
          {initial}
        </Text>
      </Box>
      <Box minW={0} flex="1">
        <Text fontSize="xs" color={colors.textSecondary} fontWeight="600">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="600" color={colors.text}>
          {name}
        </Text>
        {engineer.user_email ? (
          <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
            {engineer.user_email}
          </Text>
        ) : null}
      </Box>
    </HStack>
  )
}

type Props = {
  primaryEngineer?: FieldEngineerSummary | null
  backupEngineer?: FieldEngineerSummary | null
  onManageSchedule?: () => void
}

export default function AssignedTeamPanel({
  primaryEngineer,
  backupEngineer,
  onManageSchedule,
}: Props) {
  const colors = useThemeColors()
  const hasEngineers = Boolean(primaryEngineer || backupEngineer)

  return (
    <OverviewSectionCard title="Assigned team">
      {hasEngineers ? (
        <VStack align="stretch" spacing={3}>
          {primaryEngineer ? <EngineerRow label="Primary engineer" engineer={primaryEngineer} /> : null}
          {backupEngineer ? <EngineerRow label="Backup engineer" engineer={backupEngineer} /> : null}
          {onManageSchedule ? (
            <Button size="sm" variant="link" colorScheme="blue" alignSelf="flex-start" onClick={onManageSchedule}>
              Manage schedule →
            </Button>
          ) : null}
        </VStack>
      ) : (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" color={colors.textSecondary}>
            No field engineers assigned yet.
          </Text>
          {onManageSchedule ? (
            <Button size="sm" colorScheme="blue" variant="outline" alignSelf="flex-start" onClick={onManageSchedule}>
              Go to schedule & assign
            </Button>
          ) : null}
        </VStack>
      )}
    </OverviewSectionCard>
  )
}
